import { load } from 'cheerio'

/**
 * Adds <span> tags around paragraph text content for paragraphs that contain actual text.
 * Empty paragraphs or paragraphs containing only whitespace are left unchanged.
 *
 * @param content - HTML content string to process
 * @param styles - CSS styles string to apply to the spans
 * @returns Processed HTML content with spans added to text-containing paragraphs
 */
export const addSpansToParagraphs = (
  content: string,
  styles: string,
): string => {
  // Load the HTML string into Cheerio
  const $ = load(content)

  // Process all <p> elements
  $('p').each((_, el) => {
    const $paragraph = $(el)

    // Get the text content and check if it contains meaningful text
    const textContent = $paragraph.text().trim()

    // Skip if paragraph is empty or contains only whitespace
    if (!textContent) {
      return
    }

    // Check if paragraph already has spans wrapping all its content
    const hasSpansAlready =
      $paragraph.children('span').length > 0 &&
      $paragraph.children('span').text().trim() === textContent

    // Skip if paragraph already has proper span structure
    if (hasSpansAlready) {
      return
    }

    // Get the current HTML content of the paragraph
    const paragraphHtml = $paragraph.html()

    // If paragraph contains only text nodes (no child elements), wrap in span
    if ($paragraph.children().length === 0) {
      $paragraph.html(
        `<span style="${styles}" data-style="${styles}">${paragraphHtml}</span>`,
      )
    } else {
      // If paragraph has mixed content (text + elements), we need to be more careful
      // Check if there are direct text nodes that need wrapping
      const contents = $paragraph.contents()
      let hasDirectTextNodes = false

      contents.each((_, node) => {
        if (node.type === 'text' && $(node).text().trim()) {
          hasDirectTextNodes = true
        }
      })

      // If there are direct text nodes, wrap the entire content in a span
      if (hasDirectTextNodes) {
        $paragraph.html(
          `<span style="${styles}" data-style="${styles}">${paragraphHtml}</span>`,
        )
      }
    }
  })

  // Return only the body content without html/head/body wrapper tags
  return $('body').html() || ''
}

export default addSpansToParagraphs
